import type { SimChangesView, SimErrands } from '@cruz/shared';
import { Portrait } from './Cast.tsx';
import { card, Meter } from './ui.tsx';

/**
 * Errands: a person to please today and three goals for the week. Today's shows above the
 * event (and says when that person is in it), what was completed shows on the outcome,
 * and the whole picture at the end of the day.
 */

const CheckIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

/** One line above the event: today's errand, and whether this is the moment. */
export function ErrandLine({ errands, cast }: { errands: SimErrands | null; cast: string[] }) {
  const d = errands?.daily;
  if (!d) return null;
  const now = !d.done && cast.includes(d.character_id);
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
        now
          ? 'border-gold bg-cream-200 dark:bg-gold/10'
          : 'border-purple/15 bg-paper dark:border-cream/15 dark:bg-night-800'
      }`}
    >
      <Portrait id={d.character_id} name={d.name} src={d.portrait} size={36} />
      <p className="flex-1 text-[0.95rem] leading-snug">
        <span className="block text-xs font-semibold tracking-[0.14em] text-gold-700 uppercase dark:text-gold-300">
          {d.done ? 'Encargo cumplido' : now ? 'Tu encargo, ahora' : 'Encargo de hoy'}
        </span>
        {d.text}
      </p>
      {d.done && (
        <span className="text-gold-700 dark:text-gold-300">
          <CheckIcon size={22} />
        </span>
      )}
    </div>
  );
}

/** On the outcome: what this choice completed, with its reward. */
export function ErrandNotes({ errands }: { errands: SimChangesView['errands'] }) {
  return errands.map((e) => (
    <li
      key={`${e.kind}:${e.text}`}
      className="animate-rise flex items-start gap-2.5 rounded-xl bg-gold/15 px-3.5 py-2.5 text-[0.98rem] font-medium text-gold-700 dark:bg-gold/10 dark:text-gold-300"
    >
      <span className="mt-0.5 flex-none">
        <CheckIcon />
      </span>
      <span>
        {e.kind === 'daily' ? 'Encargo cumplido' : `Objetivo de la semana: ${e.text}`}
        <span className="font-semibold"> · {e.reward}</span>
      </span>
    </li>
  ));
}

/** At the end of the day: today's errand and the week's goals. */
export function ErrandsCard({
  errands,
  dayOver,
}: {
  errands: SimErrands | null;
  /** Today's four events are played (after 6:00 the errand is already tomorrow's) */
  dayOver: boolean;
}) {
  if (!errands) return null;
  const d = dayOver ? errands.daily : null;
  const all = errands.weekly.every((g) => g.done);
  return (
    <section aria-labelledby="errands" className={`${card} px-5 py-4`}>
      <h2 id="errands" className="font-serif text-xl font-semibold">
        Encargos
      </h2>
      {d && (
        <p className="mt-2 flex items-start gap-2 text-[0.98rem]">
          {d.done ? (
            <>
              <span className="mt-0.5 text-gold-700 dark:text-gold-300">
                <CheckIcon />
              </span>
              <span>
                Hoy: cumplido. <span className="text-ink-soft dark:text-cream/70">{d.reward}</span>
              </span>
            </>
          ) : (
            <span className="text-ink-soft dark:text-cream/75">
              Hoy no ha podido ser. Mañana, otro encargo.
            </span>
          )}
        </p>
      )}
      <h3 className="mt-4 text-sm font-semibold tracking-[0.12em] text-gold-700 uppercase dark:text-gold-300">
        Esta semana
      </h3>
      <ul className="mt-2 space-y-3">
        {errands.weekly.map((g) => (
          <li key={g.id}>
            <p className="mb-1 flex items-baseline justify-between gap-3 text-[0.98rem]">
              <span className={g.done ? 'font-medium' : ''}>{g.text}</span>
              <span className="flex-none text-sm text-ink-soft tabular dark:text-cream/70">
                {g.done ? (
                  <span className="text-gold-700 dark:text-gold-300">{g.reward}</span>
                ) : (
                  `${g.have} de ${g.need}`
                )}
              </span>
            </p>
            <Meter value={(g.have / g.need) * 100} label={`${g.text}: ${g.have} de ${g.need}`} />
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm text-ink-soft dark:text-cream/70">
        {all
          ? '¡Semana completa! El lunes, objetivos nuevos.'
          : 'Sin prisa: lo que no llegue, el lunes empieza de cero.'}
      </p>
    </section>
  );
}
